import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { BasketItem } from "../../../models/BasketItem";
import useFacility from "../../../../utils/hooks/useFacility";
import {
  sendAddToCartEvent,
  sendRemoveFromCartEvent,
  convertBasketItemToGtmItem,
} from "../../../../utils/Analitics/GtmEvents";

/**
 * Globalny hook do automatycznego wykrywania zmian w koszyku i wysyłania eventów GTM.
 * Czyta pozycje ze store — bez osobnego GET /basket.
 */
export default function useGlobalBasketGtmEvents() {
  const items = useSelector((state: State) => state.basket.items);
  const hasFetched = useSelector((state: State) => state.basket.hasFetched);
  const facilityIds = useFacility();
  const previousBasketRef = useRef<BasketItem[]>([]);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasFetched) {
      return;
    }

    const currentBasket = items;
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      previousBasketRef.current = [...currentBasket];
      return;
    }

    detectBasketChanges(previousBasketRef.current, currentBasket, facilityIds);
    previousBasketRef.current = [...currentBasket];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hasFetched]);
}

const detectBasketChanges = (oldBasket: BasketItem[], newBasket: BasketItem[], facilityIds: string) => {
  if (oldBasket.length === 0 && newBasket.length === 0) return;

  newBasket.forEach((newItem) => {
    const oldItem = oldBasket.find((item) => item.assortIds === newItem.assortIds);

    if (!oldItem) {
      sendAddToCartEvent(facilityIds, {
        currency: "PLN",
        value: newItem.price * newItem.qnt,
        items: [convertBasketItemToGtmItem(newItem)],
      });
    } else if (oldItem.qnt < newItem.qnt) {
      const quantityDiff = newItem.qnt - oldItem.qnt;
      sendAddToCartEvent(facilityIds, {
        currency: "PLN",
        value: newItem.price * quantityDiff,
        items: [
          {
            ...convertBasketItemToGtmItem(newItem),
            quantity: quantityDiff,
          },
        ],
      });
    }
  });

  oldBasket.forEach((oldItem) => {
    const newItem = newBasket.find((item) => item.assortIds === oldItem.assortIds);

    if (!newItem) {
      sendRemoveFromCartEvent(facilityIds, {
        currency: "PLN",
        value: oldItem.price * oldItem.qnt,
        items: [convertBasketItemToGtmItem(oldItem)],
      });
    } else if (oldItem.qnt > newItem.qnt) {
      const quantityDiff = oldItem.qnt - newItem.qnt;
      sendRemoveFromCartEvent(facilityIds, {
        currency: "PLN",
        value: oldItem.price * quantityDiff,
        items: [
          {
            ...convertBasketItemToGtmItem(oldItem),
            quantity: quantityDiff,
          },
        ],
      });
    }
  });
};
